import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

//custom hooks
import { useFetch } from "../hooks/useFetch";

//constants
import { API_URLS } from "../Constants";

//context
import { useAuth } from "./AuthContext";

const BANNER_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
const RoadwaysBannerContext = createContext();
export function useRoadwaysBannerMsg() {
    return useContext(RoadwaysBannerContext);
}

export function RoadwaysBannerProvider({ children }) {
    const { auth } = useAuth();
    const fetcher = useFetch("get", API_URLS["API_GET_ALL_ROADWAYS"]);
    const [roadwaysBannerMsg, setRoadwaysBannerMsg] = useState({ text: "", link: null });
    const lastFetchedAt = useRef(null);
    const location = useLocation();

    useEffect(() => {
        if (!auth?.user) {
            lastFetchedAt.current = null; //reset cache timer on logout
            if (roadwaysBannerMsg?.text !== "")
                setRoadwaysBannerMsg({ text: "", link: null });
            return;
        }

        //check for public pages that don't require the banner message
        const isPublicPage = ["/login", "/reset-password", "/", "/top100Roadways"].includes(location.pathname);
        if (isPublicPage) {
            if (roadwaysBannerMsg?.text !== "")
                setRoadwaysBannerMsg({ text: "", link: null });
            return;
        }

        //only fetch if never fetched or TTL has expired
        const now = Date.now();
        if (lastFetchedAt.current && now - lastFetchedAt.current < BANNER_CACHE_TTL_MS) {
            return;
        }
        lastFetchedAt.current = now;
        fetcher.setRequest({});
    }, [location.pathname, auth?.user]);

    useEffect(() => {
        if (!auth?.user) return;
        const response = fetcher.serverResponse;
        if (response?.success && response?.data?.top105RoadwaysWithNoProjects?.length > 0) {
            const roadwaysCount = response.data.top105RoadwaysWithNoProjects.length;
            if (roadwaysCount > 0) { //set message only if there are roadways to report
                setRoadwaysBannerMsg({
                    text: (
                        <>
                            No projects associated with <strong>{roadwaysCount}</strong> of the Top 105 most congested road segments. For more details, click
                        </>
                    ),
                    link: { label: " here.", to: "/view-roadways" }
                });
            }
            else
                setRoadwaysBannerMsg({ text: "", link: null });
        }
    }, [fetcher.serverResponse, auth?.user]);
    return (
        <RoadwaysBannerContext.Provider value={roadwaysBannerMsg}>
            {children}
        </RoadwaysBannerContext.Provider>
    );
}
